import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SearchInput from "../SearchInput";
import ShortOffCanvasButton from "../ShortOffCanvasButton";
import ActiveFilters from "../filter/ActiveFilters";
import HealthCentersTable from "../HealthCenterTable";
import HealthCenterDetails from "../HealthCenterDetails";
import { getAllHealthCenters, deleteHealthCenter } from "../../../pages/DoctorManagement/HealthCenters/HealthCenterServices";
import useHealthCenterFilters from "../../../helpers/healthCenterHooks/useHealthCenterFilters";
import HealthCenterKPI from "../newComponents/HealthCenterKPI";
import DropDownButton from "../DDButton";
import { utils, writeFile } from "xlsx";
import LargeOffCanvasButton from "../LargeOffCanvasButton";

const HealthCentersTableWithFilters = () => {
    const [allCenters, setAllCenters] = useState([]);
    const [filteredCenters, setFilteredCenters] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [userCoords, setUserCoords] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [viewDetails, setViewDetails] = useState(null);

    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedSpecialities, setSelectedSpecialities] = useState([]);
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [selectedInsurance, setSelectedInsurance] = useState([]);
    const [selectedMedicaid, setSelectedMedicaid] = useState([]);
    const [selectedAccessibility, setSelectedAccessibility] = useState([]);
    const [selectedRating, setSelectedRating] = useState(null);
    const [selectedHours, setSelectedHours] = useState([]);
    const [centerType, setCenterType] = useState("Any");
    const [selectedAwards, setSelectedAwards] = useState("Any");
    const [selectedQualityAndAccreditation, setSelectedQualityAndAccreditation] = useState("Any");
    const [location, setLocation] = useState("");
    const [distance, setDistance] = useState(0);
    const [loading, setLoading] = useState(false);

    const filters = {
        selectedServices, setSelectedServices,
        selectedSpecialities, setSelectedSpecialities,
        selectedDepartments, setSelectedDepartments,
        selectedLanguages, setSelectedLanguages,
        selectedInsurance, setSelectedInsurance,
        selectedMedicaid, setSelectedMedicaid,
        selectedAccessibility, setSelectedAccessibility,
        selectedRating, setSelectedRating,
        selectedHours, setSelectedHours,
        centerType, setCenterType,
        selectedAwards, setSelectedAwards,
        selectedQualityAndAccreditation, setSelectedQualityAndAccreditation,
        location, setLocation,
        distance, setDistance,
        searchTerm, setSearchTerm // ✅ Ya estaba incluido
    };

    const filtered = useHealthCenterFilters({
        allCenters,
        ...filters,
        userCoords
    });

    useEffect(() => {
        setFilteredCenters(filtered);
    }, [filtered]);

    const toArray = (value) => {
        if (Array.isArray(value)) return value;
        if (typeof value === 'string') return value.split(',').map(v => v.trim()).filter(Boolean);
        return [];
    };

    const toNumber = (value) => {
        const num = Number(value);
        return isNaN(num) ? 0 : num;
    };

    const loadCenters = async () => {
        try {
            const data = await getAllHealthCenters();
            const parsedData = data.map(center => ({
                ...center,
                services: toArray(center.services),
                specialties: toArray(center.specialties),
                departments: toArray(center.departments),
                languages: toArray(center.languages),
                insurance: toArray(center.insurance),
                medicaid: center.medicaid === true ? ["Yes"] : [],
                accessibility: toArray(center.accessibility),
                hours: toArray(center.hours),
                rating: toNumber(center.rating),
            }));
            setAllCenters(parsedData);
        } catch (error) {
            console.error("❌ Error loading centers:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleMassDelete = async () => {
        if (!selectedRows.length) return alert("No centers selected.");
        if (!window.confirm(`Delete ${selectedRows.length} centers?`)) return;

        let success = 0;
        for (const center of selectedRows) {
            try {
                await deleteHealthCenter(center._id);
                success++;
            } catch (e) {
                console.error("❌ Error deleting", center.name);
            }
        }

        alert(`${success} centers deleted.`);
        setRowSelection({});
        loadCenters();
    };

    const exportToCSV = () => {
        if (!selectedRows.length) return alert("No centers selected.");
        const headers = Object.keys(selectedRows[0]);
        const content = [
            headers.join(","),
            ...selectedRows.map(row => headers.map(h => `"${(row[h] ?? "").toString().replace(/"/g, '""')}"`).join(","))
        ].join("\n");

        const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "selected_centers.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const exportToXLSX = () => {
        const worksheet = utils.json_to_sheet(selectedRows);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "HealthCenters");
        writeFile(workbook, "selected_centers.xlsx");
    };

    const exportToJSON = () => {
        const blob = new Blob([JSON.stringify(selectedRows, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "selected_centers.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    useEffect(() => {
        loadCenters();
    }, []);

    return (
        <div>
            {/* Body Container */}
                    <div className="container-fluid px-lg-5">

                        {/* Breadcrumb */}
                        <nav className="breadcrumb-container d-none d-md-block pt-3 mb-2" aria-label="breadcrumb">
                            <ol className="breadcrumb fs-sm mb-0">
                                <li className="breadcrumb-item"><Link to="#">Dashboard</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Health Centers</li>
                            </ol>
                        </nav>
            <HealthCenterKPI centers={filteredCenters} />

            <div className="d-flex justify-content-between align-items-center mt-4 mb-3">
                <SearchInput
                    placeHolder="Search centers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="col-md-7 col-lg-6 d-flex justify-content-end gap-1 mt-2 gap-sm-2 gy-3 flex-wrap">
                    <DropDownButton
                        actions={[
                            { label: "Asign Tags", icon: "🏷️" },
                            { label: "Delete", icon: "🗑️", onClick: handleMassDelete },
                            { label: "Send SMS", icon: "🔔" },
                            { label: "Send Mail", icon: "✉️" },
                            { label: "CSV", icon: "📊", onClick: exportToCSV },
                            { label: "XLSX", icon: "📗", onClick: exportToXLSX },
                            { label: "JSON", icon: "📄", onClick: exportToJSON },
                        ]}
                    />
                    <ShortOffCanvasButton                        
                        title="Filter Health Centers"
                        {...filters}
                        setUserCoords={setUserCoords}
                    />
                    
                    <LargeOffCanvasButton
                        onCenterCreated={loadCenters}
                    />
                </div>
            </div>

            {/* ✅ CLAVE: Pasar todos los filtros incluyendo searchTerm */}
            <ActiveFilters 
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
                selectedSpecialities={selectedSpecialities}
                setSelectedSpecialities={setSelectedSpecialities}
                selectedDepartments={selectedDepartments}
                setSelectedDepartments={setSelectedDepartments}
                selectedLanguages={selectedLanguages}
                setSelectedLanguages={setSelectedLanguages}
                selectedInsurance={selectedInsurance}
                setSelectedInsurance={setSelectedInsurance}
                selectedMedicaid={selectedMedicaid}
                setSelectedMedicaid={setSelectedMedicaid}
                selectedAccessibility={selectedAccessibility}
                setSelectedAccessibility={setSelectedAccessibility}
                selectedRating={selectedRating}
                setSelectedRating={setSelectedRating}
                selectedHours={selectedHours}
                setSelectedHours={setSelectedHours}
                centerType={centerType}
                setCenterType={setCenterType}
                selectedAwards={selectedAwards}
                setSelectedAwards={setSelectedAwards}
                selectedQualityAndAccreditation={selectedQualityAndAccreditation}
                setSelectedQualityAndAccreditation={setSelectedQualityAndAccreditation}
                location={location}
                setLocation={setLocation}
                distance={distance}
                setDistance={setDistance}
            />

            <HealthCentersTable
                data={filteredCenters}
                onSelectionChange={setSelectedRows}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
                onCenterDeleted={loadCenters}
                onViewDetails={setViewDetails}
            />

            {viewDetails && (
                <HealthCenterDetails
                    center={viewDetails}
                    show={!!viewDetails}
                    onClose={() => setViewDetails(null)}
                />
            )}
            </div>
        </div>
    );
};

export default HealthCentersTableWithFilters;